import { useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { api } from '@/api/client'
import type { GitHubAccount, User } from '@/lib/types'
import { LEGO_CHARACTERS, PICKABLE_LEGO_CHARACTERS, legoAvatarUrl } from '@/lib/legoAvatars'
import { Avatar } from './Avatar'

/** A GitHub username as GitHub allows it; the server checks it again and resolves its id. */
const GITHUB_LOGIN = /^[A-Za-z0-9](?:[A-Za-z0-9]|-(?=[A-Za-z0-9])){0,38}$/

type Check =
  | { state: 'idle' | 'checking' }
  | { state: 'ok'; account: GitHubAccount & { linked_to?: string | null } }
  | { state: 'error'; message: string }

/**
 * A developer's profile: a LEGO Star Wars: The Complete Saga portrait and the GitHub account
 * their commits are matched to. Both are required; until both are set the card opens on its own
 * and cannot be dismissed. The sidebar account button opens it to change either. Each character
 * and each GitHub account belongs to one person.
 */
export function DeveloperProfileDialog({ profile, onSaved, onClose }: {
  profile: User
  onSaved: (u: User) => void
  onClose: () => void
}) {
  const dialog = useRef<HTMLDialogElement>(null)
  const id = useId()
  const currentLego = profile.avatar?.kind === 'lego' ? profile.avatar.value : null
  const currentLogin = profile.github?.login ?? ''
  const [choice, setChoice] = useState<string | null>(currentLego)
  const [query, setQuery] = useState('')
  const [login, setLogin] = useState(currentLogin)
  const [check, setCheck] = useState<Check>(profile.github ? { state: 'ok', account: profile.github } : { state: 'idle' })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [taken, setTaken] = useState<Record<string, string>>({})
  const required = !profile.avatar?.value || !profile.github
  const name = profile.name || profile.email
  const chosen = LEGO_CHARACTERS.find(c => c.id === choice)
  const q = query.trim().toLowerCase()
  const shown = q ? PICKABLE_LEGO_CHARACTERS.filter(c => c.name.toLowerCase().includes(q)) : PICKABLE_LEGO_CHARACTERS
  const typed = login.trim()
  const loginChanged = typed.toLowerCase() !== currentLogin.toLowerCase()
  const githubReady = check.state === 'ok' && !check.account.linked_to
  const changed = choice !== currentLego || loginChanged

  useEffect(() => {
    const element = dialog.current!
    element.showModal()
    return () => element.close()
  }, [])

  const loadTaken = () => api.get<{ taken: Record<string, string> }>('/lego-avatars')
    .then(r => { setTaken(r.taken); return r.taken })
  useEffect(() => { void loadTaken().catch(() => { /* the save still enforces uniqueness */ }) }, [])

  // Look the username up shortly after typing stops, so a typo shows before saving.
  useEffect(() => {
    if (!loginChanged && profile.github) { setCheck({ state: 'ok', account: profile.github }); return }
    if (!typed) { setCheck({ state: 'idle' }); return }
    if (!GITHUB_LOGIN.test(typed)) { setCheck({ state: 'error', message: 'That is not a valid GitHub username.' }); return }
    setCheck({ state: 'checking' })
    let live = true
    const timer = setTimeout(() => {
      api.get<GitHubAccount & { linked_to: string | null }>(`/github-accounts/${encodeURIComponent(typed)}`)
        .then(account => { if (live) setCheck({ state: 'ok', account }) })
        .catch(e => { if (live) setCheck({ state: 'error', message: e instanceof Error ? e.message : 'Could not check this username.' }) })
    }, 400)
    return () => { live = false; clearTimeout(timer) }
  }, [typed, loginChanged, profile.github])

  async function save() {
    if (!choice || !githubReady) return
    setBusy(true); setError('')
    const body: Record<string, unknown> = {}
    if (choice !== currentLego) body.avatar = { kind: 'lego', value: choice }
    if (loginChanged) body.github = { login: typed }
    try { onSaved(await api.put<User>('/me', body)) }
    catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save your profile.')
      setBusy(false)
      // Someone may have taken the character meanwhile: refresh, and drop the pick if it is gone.
      void loadTaken().then(now => { if (now[choice]) setChoice(null) }).catch(() => {})
    }
  }

  return createPortal(<dialog ref={dialog} className="review-confirmation lego-avatar-dialog" aria-labelledby={`${id}-title`}
    onCancel={event => { event.preventDefault(); if (!busy && !required) onClose() }}
    // Browsers may close a modal on repeated Escape despite preventDefault; a required setup reopens it.
    onClose={event => { const element = event.currentTarget; if (required && element.isConnected && !element.open) element.showModal() }}>
    <div className="lego-avatar-head">
      <Avatar avatar={choice ? { kind: 'lego', value: choice } : null} name={name} size={64} />
      <div>
        <h2 id={`${id}-title`}>{required ? 'Set up your developer profile' : 'Your developer profile'}</h2>
        <p className="muted small">{required
          ? 'Both steps are required to continue. Each character and each GitHub account can belong to only one person.'
          : 'Change your picture or the GitHub account your commits are matched to.'}</p>
      </div>
    </div>

    <section className="developer-step" aria-labelledby={`${id}-picture`}>
      <h3 id={`${id}-picture`}><span className="developer-step-number">1</span>Profile picture
        {chosen && <span className="developer-step-done">✓ {chosen.name}</span>}</h3>
      <input type="search" placeholder="Search characters" aria-label="Search characters" value={query} onChange={e => setQuery(e.target.value)} />
      <div className="lego-avatar-grid" role="listbox" aria-label="Characters">
        {shown.map(c => {
          const owner = taken[c.id]
          return <button type="button" key={c.id} role="option" aria-selected={choice === c.id} aria-disabled={Boolean(owner)}
            title={owner ? `${c.name} · taken by ${owner}` : c.name} disabled={Boolean(owner)}
            className={choice === c.id ? 'on' : owner ? 'taken' : ''} onClick={() => setChoice(c.id)}>
            <img src={legoAvatarUrl(c.id)} alt={owner ? `${c.name}, taken by ${owner}` : c.name} loading="lazy" width={56} height={56} />
          </button>
        })}
        {!shown.length && <p className="muted small">No character matches “{query}”.</p>}
      </div>
    </section>

    <section className="developer-step" aria-labelledby={`${id}-github`}>
      <h3 id={`${id}-github`}><span className="developer-step-number">2</span>GitHub account
        {githubReady && <span className="developer-step-done">✓ @{check.account.login}</span>}</h3>
      <label className="github-login">
        <span className="muted">github.com/</span>
        <input value={login} onChange={e => setLogin(e.target.value)} placeholder="your-username" aria-label="GitHub username"
          autoComplete="off" autoCapitalize="off" spellCheck={false} maxLength={39} />
      </label>
      <div className="github-check" aria-live="polite">
        {check.state === 'checking' && <span className="muted small">Checking GitHub…</span>}
        {check.state === 'error' && <span className="field-error">{check.message}</span>}
        {check.state === 'ok' && <span className="github-account">
          {check.account.avatar_url && <img src={check.account.avatar_url} alt="" width={28} height={28} />}
          <span><strong>{check.account.name || check.account.login}</strong> <span className="muted small">@{check.account.login}</span></span>
          {check.account.linked_to && <span className="field-error">Already linked to {check.account.linked_to}.</span>}
        </span>}
        {check.state === 'idle' && <span className="muted small">Used to match your commits to you.</span>}
      </div>
    </section>

    {error && <p className="note err" role="alert">{error}</p>}
    <div className="row">
      {!required && <button disabled={busy} onClick={onClose}>Cancel</button>}
      <button className="primary" disabled={busy || !choice || !githubReady || !changed} onClick={() => void save()}>
        {busy ? 'Saving…' : 'Save profile'}
      </button>
    </div>
  </dialog>, document.body)
}
